import { Platform } from 'react-native'
import Constants from './Constants'
import GlobalConfig from './GlobalConfig'

export const colors = {
  white: 'white',
  transparent: 'transparent',
  black: 'black',
  lightBlack: '#212121',
  mediumBlack: '#151515',
  lightGrey: '#f7f7f7',
  grey: '#bdbec2',
  darkGrey: '#8e8e93',
  darkGreyLightTheme: '#6e6e73',
  veryDarkGrey: '#424242',
  veryDarkGreyDarkTheme: '#3a3a3c',
  veryDarkBlueGrey: '#37474f',
  lightYellow: '#fffde7',
  lightRed: '#FFEBEE',
  lightBlue: '#e3f2fd',
  mediumBlue: '#BBDEFB',
  darkBlue: '#006db3',
  lightBlueGrey: '#ECEFF1',
  transluscentWhite: '#bdbec288',
  transluscentBlack: '#00000088',
  indigo: '#5e5ce6',
  lightTeal: '#d5edec',
  darkOrange: '#ff702b',
  deepPurple: '#673ab7',
  brick: '#cb4154',
  green50: '#2E7D32',
  ...Platform.select({
    ios: {
      green: '#066839',
      red: '#ff3b30',
      redLightMode: '#ff3b30',
      redDarkMode: '#ff3b30',
      yellow: '#ffcc00',
      orange: '#ff6a00',
      blue: '#007aff',
      transluscentBlue: '#007aff66',
      transluscentRed: '#ff3b3066',
      lightGreen: '#B2DFDB'
    },
    android: {
      green: '#00897B',
      red: '#E53935',
      redLightMode: '#E53935',
      redDarkMode: '#F44336',
      yellow: '#FDD835',
      orange: '#ff6a00',
      blue: '#039BE5',
      transluscentBlue: '#039BE566',
      transluscentRed: '#E5393566',
      lightGreen: '#B2DFDB'
    },
    web: {
      green: '#00897B',
      red: '#E53935',
      redLightMode: '#E53935',
      redDarkMode: '#F44336',
      yellow: '#FDD835',
      orange: '#ff6a00',
      blue: '#039BE5',
      transluscentBlue: '#039BE566',
      transluscentRed: '#E5393566',
      lightGreen: '#B2DFDB'
    }
  })
}

// Each palette entry defines the primary-derived colors for light and dark modes.
// Add new entries here to extend the available theme colors — no other changes needed.
export const colorPalette = [
  {
    id: 'blue',
    light: {
      primary: colors.blue,
      lightPrimary: colors.lightBlue,
      darkPrimary: colors.darkBlue,
      transluscentPrimary: colors.transluscentBlue,
      tileHeader: colors.mediumBlue
    },
    dark: {
      primary: colors.blue,
      lightPrimary: colors.veryDarkGrey,
      darkPrimary: colors.darkBlue,
      transluscentPrimary: colors.transluscentBlue,
      tileHeader: colors.darkBlue
    }
  },
  {
    id: 'teal',
    light: {
      primary: '#00897B',
      lightPrimary: '#E0F2F1',
      darkPrimary: '#00695C',
      transluscentPrimary: '#00897B66',
      tileHeader: '#B2DFDB'
    },
    dark: {
      primary: '#00897B',
      lightPrimary: colors.veryDarkGrey,
      darkPrimary: '#00695C',
      transluscentPrimary: '#00897B66',
      tileHeader: '#00695C'
    }
  },
  {
    id: 'purple',
    light: {
      primary: '#7B1FA2',
      lightPrimary: '#F3E5F5',
      darkPrimary: '#4A148C',
      transluscentPrimary: '#7B1FA266',
      tileHeader: '#E1BEE7'
    },
    dark: {
      primary: '#AB47BC',
      lightPrimary: colors.veryDarkGrey,
      darkPrimary: '#4A148C',
      transluscentPrimary: '#AB47BC66',
      tileHeader: '#4A148C'
    }
  },
  {
    id: 'rose',
    light: {
      primary: '#C2185B',
      lightPrimary: '#FCE4EC',
      darkPrimary: '#880E4F',
      transluscentPrimary: '#C2185B66',
      tileHeader: '#F8BBD0'
    },
    dark: {
      primary: '#EC407A',
      lightPrimary: colors.veryDarkGrey,
      darkPrimary: '#880E4F',
      transluscentPrimary: '#EC407A66',
      tileHeader: '#880E4F'
    }
  },
  {
    id: 'brown',
    light: {
      primary: '#6D4C41',
      lightPrimary: '#EFEBE9',
      darkPrimary: '#4E342E',
      transluscentPrimary: '#6D4C4166',
      tileHeader: '#D7CCC8'
    },
    dark: {
      primary: '#8D6E63',
      lightPrimary: colors.veryDarkGrey,
      darkPrimary: '#4E342E',
      transluscentPrimary: '#8D6E6366',
      tileHeader: '#4E342E'
    }
  },
  {
    id: 'indigo',
    light: {
      primary: '#303F9F',
      lightPrimary: '#E8EAF6',
      darkPrimary: '#1A237E',
      transluscentPrimary: '#303F9F66',
      tileHeader: '#C5CAE9'
    },
    dark: {
      primary: '#5C6BC0',
      lightPrimary: colors.veryDarkGrey,
      darkPrimary: '#1A237E',
      transluscentPrimary: '#5C6BC066',
      tileHeader: '#1A237E'
    }
  }
]

const colorThemes = (function () {
  const themes = {
    light: {
      lightTint: colors.lightGrey,
      mediumTint: colors.grey,
      darkTint: colors.darkGreyLightTheme,
      lightBlueGreyTint: colors.lightBlueGrey,
      transluscentBackground: colors.transluscentBlack,
      transluscentText: colors.transluscentWhite,
      textColor: colors.lightBlack,
      screenBackgroundColor: colors.white,
      textBackgroundColor: colors.white,
      listItemSelectedColor: colors.lightYellow,
      formFieldIcon: colors.grey,
      primary: colors.blue,
      primaryBusiness: colors.green50,
      primaryEnterprise: colors.deepPurple,
      lightPrimary: colors.lightBlue,
      tileHeader: colors.mediumBlue,
      transluscentPrimary: colors.transluscentBlue,
      tabBarBackgroundColor: colors.lightGrey,
      tabBarButtonColor: colors.darkGreyLightTheme,
      tabBarSelectedButtonColor: colors.blue,
      navBarBackgroundColor: colors.blue,
      navBarTextColor: colors.white,
      navBarButtonColor: colors.white,
      navBarSubtitleTextColor: colors.lightGrey,
      darkPrimary: colors.darkBlue,
      tileShadowColor: colors.darkGrey,
      outlineButtonBackgroundColor: colors.lightGrey,
      warningTileBackgroundColor: colors.lightRed,
      warningTileTextColor: colors.red,
      warningTextColor: colors.redLightMode,
      statusBarColor: colors.darkBlue,
      fabBackground: colors.transluscentBlack,
      enabledSwitchTrackColor: colors.lightGreen,
      alarmCategoryColors: [
        { code: '#F8BBD0', id: 'pink' },
        { code: '#BBDEFB', id: 'blue' },
        { code: '#D1C4E9', id: 'deepPurple' },
        { code: '#D7CCC8', id: 'brown' },
        { code: '#C8E6C9', id: 'green' },
        { code: '#FFE0B2', id: 'orange' },
        { code: '#B2EBF2', id: 'cyan' },
        { code: '#C5CAE9', id: 'indigo' },
        { code: '#F0F4C3', id: 'lime' },
        { code: '#B2DFDB', id: 'teal' },
        { code: '#FF5349', id: 'bloodOrange' },
        { code: '#FBBC05', id: 'calendarYellow' },
        { id: 'skyBlue', code: '#4FC3F7' },
        { id: 'amber', code: '#FFD54F' },
        { id: 'seaGreen', code: '#80CBC4' },
        { id: 'lavender', code: '#E1BEE7' },
        { id: 'peach', code: '#FFCCBC' },
        { id: 'lightCoral', code: '#FFAB91' },
        { id: 'coolGray', code: '#CFD8DC' },
        { code: '#ECEFF1', id: 'uncategorized' }
      ],
      shadowColor: colors.grey,
      tilePositiveActionBackgroundColor: colors.lightTeal,
      tilePositiveActionTextColor: colors.veryDarkBlueGrey,
      priceTextColor: '#00897B'
    },

    dark: {
      lightTint: colors.veryDarkGreyDarkTheme,
      mediumTint: colors.darkGrey,
      darkTint: colors.grey,
      lightBlueGreyTint: colors.veryDarkBlueGrey,
      transluscentBackground: colors.transluscentBlack,
      transluscentText: colors.transluscentWhite,
      textColor: colors.lightGrey,
      screenBackgroundColor: colors.black,
      textBackgroundColor: colors.lightBlack,
      listItemSelectedColor: colors.lightBlack,
      formFieldIcon: colors.grey,
      primary: colors.blue,
      lightPrimary: colors.veryDarkGrey,
      tileHeader: colors.darkBlue,
      transluscentPrimary: colors.transluscentBlue,
      tabBarBackgroundColor: colors.mediumBlack,
      tabBarButtonColor: colors.darkGrey,
      tabBarSelectedButtonColor: colors.blue,
      navBarBackgroundColor: colors.mediumBlack,
      navBarTextColor: colors.lightGrey,
      navBarSubtitleTextColor: colors.grey,
      navBarButtonColor: colors.lightGrey,
      darkPrimary: colors.darkBlue,
      tileShadowColor: colors.veryDarkGrey,
      outlineButtonBackgroundColor: colors.lightGrey,
      warningTileBackgroundColor: colors.veryDarkGrey,
      warningTileTextColor: colors.red,
      warningTextColor: colors.redDarkMode,
      statusBarColor: colors.black,
      fabBackground: colors.transluscentWhite,
      enabledSwitchTrackColor: colors.lightTint,
      alarmCategoryColors: [
        { code: '#C2185B', id: 'pink' },
        { code: '#0277BD', id: 'blue' },
        { code: '#7E57C2', id: 'deepPurple' },
        { code: '#8D6E63', id: 'brown' },
        { code: '#2E7D32', id: 'green' },
        { code: '#E64A19', id: 'orange' },
        { code: '#00838F', id: 'cyan' },
        { code: '#5C6BC0', id: 'indigo' },
        { code: '#9E9D24', id: 'lime' },
        { code: '#00695C', id: 'teal' },
        { code: '#FF5349', id: 'bloodOrange' },
        { code: '#FBBC05', id: 'calendarYellow' },
        { id: 'skyBlue', code: '#4FC3F7' },
        { id: 'amber', code: '#FFD54F' },
        { id: 'seaGreen', code: '#80CBC4' },
        { id: 'lavender', code: '#E1BEE7' },
        { id: 'peach', code: '#FFCCBC' },
        { id: 'lightCoral', code: '#FFAB91' },
        { id: 'coolGray', code: '#CFD8DC' },
        { code: '#455A64', id: 'uncategorized' }
      ],
      shadowColor: colors.veryDarkGrey,
      tilePositiveActionBackgroundColor: colors.veryDarkBlueGrey,
      tilePositiveActionTextColor: colors.lightGrey,
      priceTextColor: '#B2DFDB'
    }
  }

  const getColorTheme = colorScheme => {
    // Based on platform get the color scheme
    if (!colorScheme) {
      if (Platform.OS === 'web') {
        colorScheme = GlobalConfig.webColorScheme
      } else colorScheme = GlobalConfig.colorScheme
    }
    if (colorScheme === Constants.COLOR_SCHEMES.AUTO_DARK) {
      colorScheme = Constants.COLOR_SCHEMES.DARK
    } else if (colorScheme === Constants.COLOR_SCHEMES.AUTO_LIGHT) {
      colorScheme = Constants.COLOR_SCHEMES.LIGHT
    }
    const base = themes[colorScheme]
    const resolvedScheme =
      colorScheme === Constants.COLOR_SCHEMES.DARK ? 'dark' : 'light'

    // Apply the user's chosen primary color palette entry.
    // All primary-derived fields are overridden here so every call site
    // automatically picks up the user's color without any changes.
    const primaryColorId = GlobalConfig.primaryColorId || 'blue'
    const paletteEntry =
      colorPalette.find(p => p.id === primaryColorId) ||
      colorPalette.find(p => p.id === 'blue') ||
      colorPalette[0]
    const palette = paletteEntry[resolvedScheme]

    return {
      ...base,
      primary: palette.primary,
      lightPrimary: palette.lightPrimary,
      darkPrimary: palette.darkPrimary,
      transluscentPrimary: palette.transluscentPrimary,
      tileHeader: palette.tileHeader,
      tabBarSelectedButtonColor: palette.primary,
      // Nav bar uses primary in light mode; in dark mode it stays mediumBlack
      navBarBackgroundColor:
        resolvedScheme === 'light'
          ? palette.primary
          : base.navBarBackgroundColor,
      // Status bar uses darkPrimary in light mode; in dark mode it stays black
      statusBarColor:
        resolvedScheme === 'light' ? palette.darkPrimary : base.statusBarColor
    }
  }

  return {
    getColorTheme,
    colors
  }
})()

export default colorThemes
